import { autocompletion, CompletionContext, CompletionResult } from '@codemirror/autocomplete';

import { TEditorFramework } from '../types';

// DOM API completions
const domCompletions = [
  { label: 'document', type: 'variable', info: 'The Document interface' },
  { label: 'window', type: 'variable', info: 'The Window interface' },
  { label: 'console', type: 'variable', info: 'Console for logging' },
  { label: 'getElementById', type: 'method', info: 'Get element by ID' },
  { label: 'querySelector', type: 'method', info: 'Query selector' },
  { label: 'querySelectorAll', type: 'method', info: 'Query all matching elements' },
  { label: 'addEventListener', type: 'method', info: 'Add event listener' },
  { label: 'removeEventListener', type: 'method', info: 'Remove event listener' },
  { label: 'createElement', type: 'method', info: 'Create new element' },
  { label: 'appendChild', type: 'method', info: 'Append child element' },
  { label: 'innerHTML', type: 'property', info: 'Element HTML content' },
  { label: 'textContent', type: 'property', info: 'Element text content' },
  { label: 'classList', type: 'property', info: 'Element class list' },
  { label: 'style', type: 'property', info: 'Element inline styles' },
  { label: 'setAttribute', type: 'method', info: 'Set attribute value' },
  { label: 'getAttribute', type: 'method', info: 'Get attribute value' },
  { label: 'fetch', type: 'function', info: 'Fetch API for HTTP requests' },
  { label: 'setTimeout', type: 'function', info: 'Delay execution' },
  { label: 'setInterval', type: 'function', info: 'Repeat at interval' },
  { label: 'clearTimeout', type: 'function', info: 'Clear timeout' },
  { label: 'clearInterval', type: 'function', info: 'Clear interval' },
  { label: 'localStorage', type: 'variable', info: 'Local storage API' },
  { label: 'sessionStorage', type: 'variable', info: 'Session storage API' },
  { label: 'JSON.parse', type: 'method', info: 'Parse JSON string' },
  { label: 'JSON.stringify', type: 'method', info: 'Convert to JSON string' },
];

// React-specific completions
const reactCompletions = [
  { label: 'useState', type: 'function', info: 'React state hook', apply: 'useState()' },
  { label: 'useEffect', type: 'function', info: 'React effect hook', apply: 'useEffect(() => {\n  \n}, [])' },
  { label: 'useRef', type: 'function', info: 'React ref hook', apply: 'useRef(null)' },
  { label: 'useMemo', type: 'function', info: 'Memoize value', apply: 'useMemo(() => {\n  \n}, [])' },
  { label: 'useCallback', type: 'function', info: 'Memoize callback', apply: 'useCallback(() => {\n  \n}, [])' },
  { label: 'useContext', type: 'function', info: 'Use context value', apply: 'useContext()' },
  { label: 'useReducer', type: 'function', info: 'State with reducer', apply: 'useReducer(reducer, initialState)' },
  { label: 'React.createElement', type: 'method', info: 'Create React element' },
  { label: 'ReactDOM.createRoot', type: 'method', info: 'Create React root' },
  { label: 'createRoot', type: 'function', info: 'Create React 18 root' },
  { label: 'render', type: 'method', info: 'Render React element' },
  { label: 'props', type: 'property', info: 'Component props' },
  { label: 'children', type: 'property', info: 'Child elements' },
  { label: 'key', type: 'property', info: 'List item key' },
  { label: 'className', type: 'property', info: 'CSS class name (JSX)' },
  { label: 'onClick', type: 'property', info: 'Click event handler' },
  { label: 'onChange', type: 'property', info: 'Change event handler' },
  { label: 'onSubmit', type: 'property', info: 'Form submit handler' },
];

// Vue-specific completions
const vueCompletions = [
  { label: 'Vue.createApp', type: 'method', info: 'Create Vue app' },
  { label: 'createApp', type: 'function', info: 'Create Vue 3 app' },
  { label: 'ref', type: 'function', info: 'Vue reactive ref', apply: 'ref()' },
  { label: 'reactive', type: 'function', info: 'Vue reactive object', apply: 'reactive({})' },
  { label: 'computed', type: 'function', info: 'Computed property', apply: 'computed(() => )' },
  { label: 'watch', type: 'function', info: 'Watch reactive source' },
  { label: 'watchEffect', type: 'function', info: 'Auto-tracking effect' },
  { label: 'onMounted', type: 'function', info: 'Mounted lifecycle hook' },
  { label: 'onUnmounted', type: 'function', info: 'Unmounted lifecycle hook' },
  { label: 'onUpdated', type: 'function', info: 'Updated lifecycle hook' },
  { label: 'defineComponent', type: 'function', info: 'Define component' },
  { label: 'defineProps', type: 'function', info: 'Define component props' },
  { label: 'defineEmits', type: 'function', info: 'Define component emits' },
  { label: 'v-if', type: 'keyword', info: 'Conditional rendering' },
  { label: 'v-for', type: 'keyword', info: 'List rendering' },
  { label: 'v-model', type: 'keyword', info: 'Two-way binding' },
  { label: 'v-on', type: 'keyword', info: 'Event listener' },
  { label: 'v-bind', type: 'keyword', info: 'Attribute binding' },
  { label: '.mount', type: 'method', info: 'Mount Vue app' },
];

// TypeScript-specific completions
const typescriptCompletions = [
  { label: 'interface', type: 'keyword', info: 'Define interface' },
  { label: 'type', type: 'keyword', info: 'Define type alias' },
  { label: 'enum', type: 'keyword', info: 'Define enumeration' },
  { label: 'string', type: 'type', info: 'String type' },
  { label: 'number', type: 'type', info: 'Number type' },
  { label: 'boolean', type: 'type', info: 'Boolean type' },
  { label: 'void', type: 'type', info: 'Void return type' },
  { label: 'null', type: 'type', info: 'Null type' },
  { label: 'undefined', type: 'type', info: 'Undefined type' },
  { label: 'any', type: 'type', info: 'Any type (avoid)' },
  { label: 'unknown', type: 'type', info: 'Unknown type' },
  { label: 'never', type: 'type', info: 'Never type' },
  { label: 'Array<T>', type: 'type', info: 'Array generic type' },
  { label: 'Record<K, V>', type: 'type', info: 'Record type' },
  { label: 'Partial<T>', type: 'type', info: 'Make all props optional' },
  { label: 'Required<T>', type: 'type', info: 'Make all props required' },
  { label: 'Pick<T, K>', type: 'type', info: 'Pick properties' },
  { label: 'Omit<T, K>', type: 'type', info: 'Omit properties' },
  { label: 'as', type: 'keyword', info: 'Type assertion' },
  { label: 'readonly', type: 'keyword', info: 'Readonly modifier' },
];

// CSS property completions
const cssCompletions = [
  { label: 'display', type: 'property', info: 'Display type' },
  { label: 'flex', type: 'property', info: 'Flex shorthand' },
  { label: 'grid', type: 'property', info: 'Grid display' },
  { label: 'position', type: 'property', info: 'Position type' },
  { label: 'margin', type: 'property', info: 'Margin shorthand' },
  { label: 'padding', type: 'property', info: 'Padding shorthand' },
  { label: 'width', type: 'property', info: 'Element width' },
  { label: 'height', type: 'property', info: 'Element height' },
  { label: 'background', type: 'property', info: 'Background shorthand' },
  { label: 'background-color', type: 'property', info: 'Background color' },
  { label: 'color', type: 'property', info: 'Text color' },
  { label: 'font-size', type: 'property', info: 'Font size' },
  { label: 'font-weight', type: 'property', info: 'Font weight' },
  { label: 'border', type: 'property', info: 'Border shorthand' },
  { label: 'border-radius', type: 'property', info: 'Border radius' },
  { label: 'box-shadow', type: 'property', info: 'Box shadow' },
  { label: 'transform', type: 'property', info: 'Transform' },
  { label: 'transition', type: 'property', info: 'Transition' },
  { label: 'animation', type: 'property', info: 'Animation' },
  { label: 'z-index', type: 'property', info: 'Stack order' },
  { label: 'overflow', type: 'property', info: 'Overflow behavior' },
  { label: 'gap', type: 'property', info: 'Grid/flex gap' },
  { label: 'justify-content', type: 'property', info: 'Main axis alignment' },
  { label: 'align-items', type: 'property', info: 'Cross axis alignment' },
  { label: 'flex-direction', type: 'property', info: 'Flex direction' },
  { label: 'flex-wrap', type: 'property', info: 'Flex wrap' },
  { label: 'align-self', type: 'property', info: 'Self alignment' },
  { label: 'text-align', type: 'property', info: 'Text alignment' },
  { label: 'text-decoration', type: 'property', info: 'Text decoration' },
  { label: 'text-transform', type: 'property', info: 'Text transform' },
  { label: 'line-height', type: 'property', info: 'Line height' },
  { label: 'letter-spacing', type: 'property', info: 'Letter spacing' },
  { label: 'opacity', type: 'property', info: 'Opacity' },
  { label: 'cursor', type: 'property', info: 'Mouse cursor' },
  { label: 'pointer-events', type: 'property', info: 'Pointer events' },
  { label: 'max-width', type: 'property', info: 'Maximum width' },
  { label: 'min-width', type: 'property', info: 'Minimum width' },
  { label: 'max-height', type: 'property', info: 'Maximum height' },
  { label: 'min-height', type: 'property', info: 'Minimum height' },
  { label: 'grid-template-columns', type: 'property', info: 'Grid columns' },
  { label: 'grid-template-rows', type: 'property', info: 'Grid rows' },
  { label: 'place-items', type: 'property', info: 'Align + justify items' },
  { label: 'object-fit', type: 'property', info: 'Object fit' },
  { label: 'aspect-ratio', type: 'property', info: 'Aspect ratio' },
];

// CSS value completions — context-aware based on property name
const cssValueMap: Record<string, { label: string; type: string }[]> = {
  display: [
    { label: 'flex', type: 'value' },
    { label: 'grid', type: 'value' },
    { label: 'block', type: 'value' },
    { label: 'inline-flex', type: 'value' },
    { label: 'inline-block', type: 'value' },
    { label: 'inline', type: 'value' },
    { label: 'none', type: 'value' },
    { label: 'contents', type: 'value' },
  ],
  position: [
    { label: 'relative', type: 'value' },
    { label: 'absolute', type: 'value' },
    { label: 'fixed', type: 'value' },
    { label: 'sticky', type: 'value' },
    { label: 'static', type: 'value' },
  ],
  'flex-direction': [
    { label: 'row', type: 'value' },
    { label: 'column', type: 'value' },
    { label: 'row-reverse', type: 'value' },
    { label: 'column-reverse', type: 'value' },
  ],
  'flex-wrap': [
    { label: 'nowrap', type: 'value' },
    { label: 'wrap', type: 'value' },
    { label: 'wrap-reverse', type: 'value' },
  ],
  'justify-content': [
    { label: 'flex-start', type: 'value' },
    { label: 'flex-end', type: 'value' },
    { label: 'center', type: 'value' },
    { label: 'space-between', type: 'value' },
    { label: 'space-around', type: 'value' },
    { label: 'space-evenly', type: 'value' },
  ],
  'align-items': [
    { label: 'flex-start', type: 'value' },
    { label: 'flex-end', type: 'value' },
    { label: 'center', type: 'value' },
    { label: 'stretch', type: 'value' },
    { label: 'baseline', type: 'value' },
  ],
  'align-self': [
    { label: 'auto', type: 'value' },
    { label: 'flex-start', type: 'value' },
    { label: 'flex-end', type: 'value' },
    { label: 'center', type: 'value' },
    { label: 'stretch', type: 'value' },
  ],
  'text-align': [
    { label: 'left', type: 'value' },
    { label: 'center', type: 'value' },
    { label: 'right', type: 'value' },
    { label: 'justify', type: 'value' },
  ],
  'text-decoration': [
    { label: 'none', type: 'value' },
    { label: 'underline', type: 'value' },
    { label: 'line-through', type: 'value' },
    { label: 'overline', type: 'value' },
  ],
  'text-transform': [
    { label: 'none', type: 'value' },
    { label: 'uppercase', type: 'value' },
    { label: 'lowercase', type: 'value' },
    { label: 'capitalize', type: 'value' },
  ],
  overflow: [
    { label: 'visible', type: 'value' },
    { label: 'hidden', type: 'value' },
    { label: 'scroll', type: 'value' },
    { label: 'auto', type: 'value' },
  ],
  cursor: [
    { label: 'pointer', type: 'value' },
    { label: 'default', type: 'value' },
    { label: 'grab', type: 'value' },
    { label: 'text', type: 'value' },
    { label: 'move', type: 'value' },
    { label: 'not-allowed', type: 'value' },
    { label: 'crosshair', type: 'value' },
  ],
  'font-weight': [
    { label: 'normal', type: 'value' },
    { label: 'bold', type: 'value' },
    { label: '100', type: 'value' },
    { label: '200', type: 'value' },
    { label: '300', type: 'value' },
    { label: '400', type: 'value' },
    { label: '500', type: 'value' },
    { label: '600', type: 'value' },
    { label: '700', type: 'value' },
    { label: '800', type: 'value' },
    { label: '900', type: 'value' },
  ],
  'object-fit': [
    { label: 'contain', type: 'value' },
    { label: 'cover', type: 'value' },
    { label: 'fill', type: 'value' },
    { label: 'none', type: 'value' },
    { label: 'scale-down', type: 'value' },
  ],
  'pointer-events': [
    { label: 'auto', type: 'value' },
    { label: 'none', type: 'value' },
  ],
};

/**
 * Get completions based on framework
 */
const getJsCompletions = (framework: TEditorFramework) => {
  const completions = [...domCompletions];

  switch (framework) {
    case 'react':
      completions.push(...reactCompletions);
      break;
    case 'vue':
      completions.push(...vueCompletions);
      break;
    case 'typescript':
      completions.push(...typescriptCompletions);
      break;
  }

  return completions;
};

/**
 * Create a completion source for JavaScript/TypeScript
 */
const jsCompletionSource = (framework: TEditorFramework) => (context: CompletionContext): CompletionResult | null => {
  const word = context.matchBefore(/\w*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  return {
    from: word.from,
    options: getJsCompletions(framework),
    validFor: /^\w*$/,
  };
};

/**
 * Create a completion source for CSS — provides property names and context-aware values
 */
const cssCompletionSource = (context: CompletionContext): CompletionResult | null => {
  const word = context.matchBefore(/[\w-]*/);
  if (!word || (word.from === word.to && !context.explicit)) return null;

  // Check if cursor is after a colon (value position)
  const lineText = context.state.doc.lineAt(word.from).text;
  const beforeWord = lineText.substring(0, word.from - context.state.doc.lineAt(word.from).from);
  const colonMatch = beforeWord.match(/^\s*([\w-]+)\s*:\s*$/);

  if (colonMatch) {
    const property = colonMatch[1];
    const values = cssValueMap[property];
    if (values) {
      return {
        from: word.from,
        options: values,
        validFor: /^[\w-]*$/,
      };
    }
  }

  return {
    from: word.from,
    options: cssCompletions,
    validFor: /^[\w-]*$/,
  };
};

/**
 * Create autocompletion extension for JS/TS based on framework
 */
export const createJsAutocomplete = (framework: TEditorFramework) => {
  return autocompletion({
    override: [jsCompletionSource(framework)],
    activateOnTyping: true,
    maxRenderedOptions: 15,
  });
};

/**
 * Create autocompletion extension for CSS
 */
export const createCssAutocomplete = () => {
  return autocompletion({
    override: [cssCompletionSource],
    activateOnTyping: true,
    maxRenderedOptions: 15,
  });
};
